import type {
  FinancialModelInputs,
  SaasInputs,
  OneTimeSalesInputs,
  ConsultingInputs,
  MonthlyData,
  FinancialModelSummary,
} from '@/types/financial-model';

const MONTHS_COUNT = 12;

// ---- SaaS Subscription Model ----

function calculateSaas(inputs: SaasInputs): MonthlyData[] {
  const months: MonthlyData[] = [];
  let prevSubscribers = 0;
  let prevCash = inputs.initialInvestment;

  for (let i = 0; i < MONTHS_COUNT; i++) {
    const reach = inputs.expectedMonthlyReach[i] ?? 0;
    const adBudget = inputs.monthlyAdBudgets[i] ?? 0;

    // Funnel
    const leads = reach * (inputs.conversionWebsiteToLead / 100);
    const freemiumUsers = leads * (inputs.conversionLeadToFreemium / 100);
    const newPaid = freemiumUsers * (inputs.conversionFreemiumToPaid / 100);

    // Churn
    const churned = i === 0 ? 0 : prevSubscribers * (inputs.monthlyChurnRate / 100);
    const totalSubscribers = Math.max(0, prevSubscribers + newPaid - churned);

    // Revenue
    const revenue = totalSubscribers * inputs.subscriptionPrice;

    // Costs
    const variableCosts = totalSubscribers * inputs.apiCostPerUser;
    const infra = inputs.monthlyInfrastructureCost;
    const processingFee = revenue * (inputs.paymentProcessingFee / 100);
    const totalExpenses = adBudget + variableCosts + infra + processingFee;

    // Profitability
    const ebitda = revenue - totalExpenses;
    const taxes = ebitda > 0 ? ebitda * (inputs.taxRate / 100) : 0;
    const netProfit = ebitda - taxes;

    // Cash flow
    const cashBalance = prevCash + netProfit;

    // Unit economics
    const cac = newPaid > 0 ? adBudget / newPaid : null;
    const arpc = totalSubscribers > 0
      ? (revenue - variableCosts) / totalSubscribers
      : null;
    const avgLifetimeMonths = inputs.monthlyChurnRate > 0
      ? 1 / (inputs.monthlyChurnRate / 100)
      : 120; // Cap at 10 years
    const ltv = arpc !== null ? arpc * avgLifetimeMonths : null;
    const ltvCacRatio = ltv !== null && cac !== null && cac > 0 ? ltv / cac : null;

    months.push({
      month: i + 1,
      revenue,
      newCustomers: newPaid,
      totalCustomers: totalSubscribers,
      churnedCustomers: churned,
      advertisingCost: adBudget,
      variableCosts,
      infrastructureCost: infra,
      paymentProcessingCost: processingFee,
      totalExpenses,
      ebitda,
      taxes,
      netProfit,
      cashBalance,
      cac,
      arpc,
      ltv,
      ltvCacRatio,
    });

    prevSubscribers = totalSubscribers;
    prevCash = cashBalance;
  }

  return months;
}

// ---- One-time Sales Model ----

function calculateOneTimeSales(inputs: OneTimeSalesInputs): MonthlyData[] {
  const months: MonthlyData[] = [];
  let cumulativeCustomers = 0;
  let prevCash = inputs.initialInvestment;

  for (let i = 0; i < MONTHS_COUNT; i++) {
    const reach = inputs.expectedMonthlyReach[i] ?? 0;
    const adBudget = inputs.monthlyAdBudgets[i] ?? 0;

    // Funnel
    const leads = reach * (inputs.conversionWebsiteToLead / 100);
    const newCustomers = leads * (inputs.conversionLeadToSale / 100);
    const repeatCustomers = i > 0
      ? cumulativeCustomers * (inputs.repeatPurchaseRate / 100) / 12
      : 0;
    const totalSales = newCustomers + repeatCustomers;

    // Revenue
    const revenue = totalSales * inputs.productPrice;

    // Costs
    const cogsTotal = totalSales * inputs.cogs;
    const infra = inputs.monthlyInfrastructureCost;
    const processingFee = revenue * (inputs.paymentProcessingFee / 100);
    const totalExpenses = adBudget + cogsTotal + infra + processingFee;

    // Profitability
    const ebitda = revenue - totalExpenses;
    const taxes = ebitda > 0 ? ebitda * (inputs.taxRate / 100) : 0;
    const netProfit = ebitda - taxes;

    // Cash flow
    const cashBalance = prevCash + netProfit;

    // Unit economics
    const cac = newCustomers > 0 ? adBudget / newCustomers : null;
    const arpc = totalSales > 0
      ? (revenue - cogsTotal) / totalSales
      : null;

    cumulativeCustomers += newCustomers;

    months.push({
      month: i + 1,
      revenue,
      newCustomers,
      totalCustomers: totalSales,
      churnedCustomers: 0,
      advertisingCost: adBudget,
      variableCosts: cogsTotal,
      infrastructureCost: infra,
      paymentProcessingCost: processingFee,
      totalExpenses,
      ebitda,
      taxes,
      netProfit,
      cashBalance,
      cac,
      arpc,
      ltv: null,
      ltvCacRatio: null,
    });

    prevCash = cashBalance;
  }

  return months;
}

// ---- Consulting/Services Model ----

function calculateConsulting(inputs: ConsultingInputs): MonthlyData[] {
  const months: MonthlyData[] = [];
  let prevCash = inputs.initialInvestment;

  for (let i = 0; i < MONTHS_COUNT; i++) {
    const reach = inputs.expectedMonthlyReach[i] ?? 0;
    const adBudget = inputs.monthlyAdBudgets[i] ?? 0;

    // Funnel
    const leads = reach * (inputs.conversionWebsiteToLead / 100);
    const potentialClients = leads * (inputs.conversionLeadToClient / 100);
    const maxCapacity = inputs.maxClientsPerMonth * (inputs.utilizationRate / 100);
    const actualClients = Math.min(potentialClients, maxCapacity);

    // Revenue
    const revenue = actualClients * inputs.avgProjectValue;

    // Costs
    const infra = inputs.monthlyInfrastructureCost;
    const processingFee = revenue * (inputs.paymentProcessingFee / 100);
    const totalExpenses = adBudget + infra + processingFee;

    // Profitability
    const ebitda = revenue - totalExpenses;
    const taxes = ebitda > 0 ? ebitda * (inputs.taxRate / 100) : 0;
    const netProfit = ebitda - taxes;

    // Cash flow
    const cashBalance = prevCash + netProfit;

    // Unit economics
    const cac = actualClients > 0 ? adBudget / actualClients : null;
    const arpc = actualClients > 0 ? revenue / actualClients : null;

    months.push({
      month: i + 1,
      revenue,
      newCustomers: actualClients,
      totalCustomers: actualClients,
      churnedCustomers: 0,
      advertisingCost: adBudget,
      variableCosts: 0,
      infrastructureCost: infra,
      paymentProcessingCost: processingFee,
      totalExpenses,
      ebitda,
      taxes,
      netProfit,
      cashBalance,
      cac,
      arpc,
      ltv: null,
      ltvCacRatio: null,
    });

    prevCash = cashBalance;
  }

  return months;
}

// ---- Summary ----

function calculateSummary(
  months: MonthlyData[],
  initialInvestment: number,
): FinancialModelSummary {
  const totalRevenue = months.reduce((s, m) => s + m.revenue, 0);
  const totalExpenses = months.reduce((s, m) => s + m.totalExpenses, 0);
  const totalNetProfit = months.reduce((s, m) => s + m.netProfit, 0);
  const finalCashBalance = months[months.length - 1]?.cashBalance ?? 0;

  // Break-even: first month where cumulative net profit >= 0
  let cumulativeProfit = 0;
  let breakEvenMonth: number | null = null;
  for (const m of months) {
    cumulativeProfit += m.netProfit;
    if (cumulativeProfit >= 0 && breakEvenMonth === null) {
      breakEvenMonth = m.month;
    }
  }

  // Average unit economics (exclude nulls)
  const cacValues = months.map(m => m.cac).filter((v): v is number => v !== null);
  const ltvValues = months.map(m => m.ltv).filter((v): v is number => v !== null);
  const ltvCacValues = months.map(m => m.ltvCacRatio).filter((v): v is number => v !== null);

  const avgCac = cacValues.length > 0
    ? cacValues.reduce((s, v) => s + v, 0) / cacValues.length
    : null;
  const avgLtv = ltvValues.length > 0
    ? ltvValues.reduce((s, v) => s + v, 0) / ltvValues.length
    : null;
  const avgLtvCacRatio = ltvCacValues.length > 0
    ? ltvCacValues.reduce((s, v) => s + v, 0) / ltvCacValues.length
    : null;

  const roi = initialInvestment > 0
    ? ((totalNetProfit) / initialInvestment) * 100
    : 0;

  return {
    breakEvenMonth,
    totalRevenue,
    totalExpenses,
    totalNetProfit,
    finalCashBalance,
    avgCac,
    avgLtv,
    avgLtvCacRatio,
    roi,
  };
}

// ---- Main Entry Point ----

export function calculateFinancialModel(inputs: FinancialModelInputs): {
  months: MonthlyData[];
  summary: FinancialModelSummary;
} {
  let months: MonthlyData[];

  switch (inputs.modelType) {
    case 'saas':
      months = calculateSaas(inputs);
      break;
    case 'one_time':
      months = calculateOneTimeSales(inputs);
      break;
    case 'consulting':
      months = calculateConsulting(inputs);
      break;
    default:
      throw new Error(`Unknown model type: ${(inputs as FinancialModelInputs).modelType}`);
  }

  const summary = calculateSummary(months, inputs.initialInvestment);

  return { months, summary };
}

// ---- Helpers for display ----

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: 0,
  }).format(Math.round(value));
}

export function formatPercent(value: number): string {
  return new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: 1,
  }).format(value) + '%';
}

export const MONTH_NAMES = [
  'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн',
  'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек',
] as const;

export const MODEL_TYPE_LABELS: Record<string, string> = {
  saas: 'SaaS / Подписка',
  one_time: 'Разовые продажи',
  consulting: 'Консалтинг / Услуги',
};
